import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
    IPipelineParameters,
    IOperationsData,
    StaffingKeyPositionData,
} from 'app/modules/pipeline/interfaces/pipeline-data';
import { ICardInfo } from 'app/modules/my-limegear/components/standalone-components/interfaces/stanalone-components.data';
import { PipelineChartOptions } from 'app/modules/shared/classes/pipeline-chart-options';
import * as Highcharts from 'highcharts';
import * as milestoneColors from 'app/modules/shared/json/colors/milestone-colors.json';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { NavigationHelpService } from 'app/modules/shared/services/navigation-help.service';
import { NoDataActionType } from 'app/modules/shared/interfaces/no-data';
import { DisplayOptions } from 'app/modules/shared/interfaces/display-options';
import { IRowItems, ITableTdItems } from 'app/modules/shared/interfaces/card-data';
import { PipelineLoanCharacteristicsService } from 'app/modules/pipeline/services/pipeline-loan-characteristics.service';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root',
})
export class PipelineOperationService {
    private serverUrl = 'https://' + window.location.host; // TODO Get url and version number from appsettings.
    private version = '0.5';
    private componentName = 'operations';
    private _opsUrl = (this.serverUrl + '/api/v{version}/{componentName}')
        .replace('{version}', this.version)
        .replace('{componentName}', this.componentName);

    constructor(
        private _http: HttpClient,
        private _reusableFunctions: ReusableFunctions,
        private _navigationHelpService: NavigationHelpService,
        private _loanCharService: PipelineLoanCharacteristicsService,
        private _router: Router
    ) {}

    setOperationsChartData(
        displayOptions: DisplayOptions[],
        hierarchyFilters: string,
        pipelineLargeChart: ICardInfo,
        action: NoDataActionType,
        staffingKey: number,
        groupId: string
    ) {
        let commonParams: IPipelineParameters = this.getDisplayOptionDataPipelineOperations(
            displayOptions,
            hierarchyFilters
        );
        commonParams.staffingKey = staffingKey.toString();
        commonParams.cardId = groupId;

        let largeCardInteriorOptions = pipelineLargeChart.largeCardInteriorOptions;
        largeCardInteriorOptions.isDataReady = false;
        largeCardInteriorOptions.hasData = false;
        pipelineLargeChart.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);

        this.getPipelineOperationsData(commonParams).subscribe(
            (res) => {
                this.prepareChart(res, pipelineLargeChart, action);
            },
            (error) => {
                this.prepareChart([], pipelineLargeChart, NoDataActionType.error);
                console.log('error: ', error);
            }
        );
    }

    getPipelineOperationsData(parameters: IPipelineParameters): Observable<IOperationsData[]> {
        const params = JSON.parse(JSON.stringify(parameters));
        return this._http.post<IOperationsData[]>(
            this._opsUrl + '/GetPipelineOperationsData/',
            params,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }

    prepareChart(positionData, cardInfo: ICardInfo, action: NoDataActionType) {
        const isAssignmentCards = cardInfo.name.includes('Assignments');
        const pipelineCharts = new PipelineChartOptions(
            this._reusableFunctions,
            this._navigationHelpService
        );
        const tempPositionData = JSON.parse(JSON.stringify(positionData));
        const staffingKeyObj: StaffingKeyPositionData = {
            Processor: 2,
            Underwriter: 3,
            Closer: 4,
        };

        let hasData = positionData.length > 0;

        let largeCardInteriorOptions = cardInfo.largeCardInteriorOptions;
        largeCardInteriorOptions.isDataReady = true;
        largeCardInteriorOptions.hasData = hasData;
        largeCardInteriorOptions.useLGScroll = true;
        largeCardInteriorOptions.showCardControlArea = true;
        largeCardInteriorOptions.action = action;
        let chartOptions;

        if (hasData) {
            if (isAssignmentCards) {
                const chartData = this.prepareOperationsAssignmentsCards(
                    positionData,
                    tempPositionData,
                    staffingKeyObj,
                    cardInfo
                );
                chartOptions = pipelineCharts.createOperationsAssignmentsBasicBar(chartData);
            } else {
                const chartData = this.prepareOperationsStatusCards(
                    positionData,
                    staffingKeyObj,
                    cardInfo
                );
                chartOptions =
                    pipelineCharts.createOperationsAssignmentsNegativeStackChart(chartData);
            }

            largeCardInteriorOptions.chart = new Highcharts.Chart(
                largeCardInteriorOptions.uniqueId,
                chartOptions
            );
        }
        cardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
    }

    prepareOperationsAssignmentsCards(
        positionData,
        tempPositionData,
        staffingKeyObj: StaffingKeyPositionData,
        cardInfo
    ) {
        const milestones: string[] = Array.from(
            new Set(tempPositionData.map((position) => position.milestone))
        );

        tempPositionData.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
        const employees: string[] = Array.from(
            new Set(tempPositionData.map((position) => position.employeeName))
        );
        const employeeIdentifiers: string[] = Array.from(
            new Set(tempPositionData.map((position) => position.employeeIdentifier))
        );

        let combinedPositionData = {};
        const refinedData = {};

        for (let milestone of milestones) {
            combinedPositionData[milestone] = {};
            refinedData[milestone] = [];
        }

        positionData.forEach((position) => {
            const employeeIdentifier =
                combinedPositionData[position.milestone][position.employeeIdentifier];
            if (employeeIdentifier) {
                combinedPositionData[position.milestone][
                    position.employeeIdentifier
                ].assignedLoans += position.assignedLoans;
            } else {
                combinedPositionData[position.milestone][position.employeeIdentifier] = position;
            }
        });

        for (let key of Object.keys(refinedData)) {
            for (let identifier of employeeIdentifiers) {
                if (combinedPositionData[key][identifier]) {
                    refinedData[key].push({
                        color: milestoneColors.default.find(
                            (milestone) =>
                                milestone?.milestone?.toLowerCase() ==
                                combinedPositionData[key][identifier].milestone?.toLowerCase()
                        )?.color,
                        y: combinedPositionData[key][identifier].assignedLoans,
                        properties: {
                            associatedData: tempPositionData.filter(
                                (data) =>
                                    data.employeeIdentifier === identifier?.toString() &&
                                    data.milestone === key
                            ),
                            staffingKey:
                                staffingKeyObj[combinedPositionData[key][identifier].position],
                            employeeIdentifier:
                                combinedPositionData[key][identifier].employeeIdentifier,
                            milestone: combinedPositionData[key][identifier].milestone,
                            name: combinedPositionData[key][identifier].employeeName,
                            position: combinedPositionData[key][identifier].position,
                        },
                    });
                } else {
                    const nonMilestoneMatchedPosition = positionData.find(
                        (p) => p.employeeIdentifier === identifier
                    );
                    refinedData[key].push({
                        y: 0,
                        properties: {
                            staffingKey: staffingKeyObj[nonMilestoneMatchedPosition.position],
                            employeeIdentifier: identifier,
                            name: nonMilestoneMatchedPosition.employeeName,
                            position: nonMilestoneMatchedPosition.position,
                        },
                    });
                }
            }
        }

        const seriesData = [];
        for (const [key, value] of Object.entries(refinedData)) {
            seriesData.push({
                name: key,
                data: value,
                color: milestoneColors.default.find(
                    (milestone) => milestone?.milestone?.toLowerCase() == key.toLowerCase()
                )?.color,
            });
        }

        return {
            title: cardInfo.name,
            xAxisCategories: employees,
            scrollAmount: employees.length * milestones.length * 15,
            milestones,
            seriesData,
        };
    }

    getDisplayOptionDataPipelineOperations(displayOptions: DisplayOptions[], hFilterJSON: string) {
        let parameters: IPipelineParameters = {
            channel: '',
            loanType: '',
            purpose: '',
            lockStatus: 'All',
            milestone: '',
            hFilterJSON: hFilterJSON ?? '',
        };
        parameters.purpose =
            displayOptions.find((option) => option.optionKey === 'loanPurpose')?.selectedOptions ??
            'All';
        parameters.loanType =
            displayOptions.find((option) => option.optionKey === 'loanCategory')?.selectedOptions ??
            'All';
        parameters.channel =
            displayOptions.find((option) => option.optionKey === 'channel')?.selectedOptions ??
            'All';
        parameters.milestone =
            displayOptions.find((option) => option.optionKey === 'activeMilestones')
                ?.selectedOptions ?? 'All';
        return parameters;
    }

    initializeLargeCard(
        largeCardInfo: ICardInfo,
        action: NoDataActionType,
        cardDrilldownUrl: string
    ) {
        let largeCardInteriorOptions = {
            action: action,
            componentTitle: largeCardInfo.name,
            searchTerm: '',
            hasData: false,
            isDataReady: false,
            showTitle: true,
            accessToUrl: cardDrilldownUrl + '/' + largeCardInfo.urlSegment,
            uniqueId: largeCardInfo.cardId,
            showCardControllers: true,
        };
        largeCardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
    }

    prepareOperationsStatusCards(
        positionData,
        staffingKeyObj: StaffingKeyPositionData,
        cardInfo: ICardInfo
    ) {
        let milestoneCategories = positionData.map((milestone) => milestone.milestone);
        const loanData = { assignedLoans: [], unassignedLoans: [] };
        const assignedLoans = positionData.map((milestone) => milestone.assignedLoans);
        const unassignedLoans = positionData.map((milestone) => milestone.unassignedLoans);
        positionData.forEach((loan) => {
            const milestone = loan.milestone;
            const staffingKey = staffingKeyObj[loan.position];
            loanData.assignedLoans.push({
                y: loan.assignedLoans * -1,
                properties: { assigned: true, milestone, staffingKey, position: loan.position },
            });
            loanData.unassignedLoans.push({
                y: loan.unassignedLoans,
                properties: {
                    assigned: false,
                    milestone,
                    staffingKey,
                    position: loan.position,
                },
            });
        });

        const largestNumber = Math.max(...assignedLoans.concat(unassignedLoans));

        return {
            title: cardInfo.name,
            categories: milestoneCategories,
            largestNumber,
            loanData,
        };
    }

    setOperationsSmallCardData(
        displayOptions: DisplayOptions[],
        hierarchyFilters: string,
        cardInfo: ICardInfo,
        action: NoDataActionType
    ) {
        let commonParams = this.getDisplayOptionDataPipelineOperations(
            displayOptions,
            hierarchyFilters
        );
        commonParams.acctLedgerType = cardInfo.description;
        commonParams.cardId = cardInfo.type;

        let cardOptions = cardInfo.card;
        cardOptions.isDataReady = false;
        cardOptions.hasData = false;
        cardInfo.card = Object.assign({}, cardOptions);

        this.getPipelineOperationsData(commonParams).subscribe(
            (res) => {
                this.prepareSmallCards(res, cardInfo, action, commonParams);
            },
            (error) => {
                this.prepareSmallCards([], cardInfo, NoDataActionType.error, commonParams);
                console.log('error: ', error);
            }
        );
    }

    setOperationsPipelineChartData(
        displayOptions: DisplayOptions[],
        hierarchyFilters: string,
        cardInfo: ICardInfo,
        action: NoDataActionType,
        lockStatus: 'L' | 'All'
    ) {
        let commonParams = this.getDisplayOptionDataPipelineOperations(
            displayOptions,
            hierarchyFilters
        );
        commonParams.cardId = cardInfo.type;
        commonParams.lockStatus = lockStatus;

        let largeCardInteriorOptions = cardInfo.largeCardInteriorOptions;
        largeCardInteriorOptions.action = action;
        largeCardInteriorOptions.hasData = false;
        largeCardInteriorOptions.isDataReady = false;
        cardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);

        this.getPipelineOperationsData(commonParams).subscribe(
            (res) => {
                this.prepareCards(res, cardInfo, action, commonParams);
            },
            (error) => {
                this.prepareCards([], cardInfo, NoDataActionType.error, commonParams);
                console.log('error: ', error);
            }
        );
    }

    prepareCards(
        data: IOperationsData[],
        cardInfo: ICardInfo,
        action: NoDataActionType,
        params: IPipelineParameters
    ) {
        if (cardInfo.description === 'pie') {
            this.prepareDonutChartData(data, cardInfo, action, params);
        } else {
            this.prepareBarChartCards(data, cardInfo, action);
        }
    }

    prepareDonutChartData(
        data: IOperationsData[],
        cardInfo: ICardInfo,
        action: NoDataActionType,
        params: IPipelineParameters
    ) {
        let hasData = data && data.length > 0;
        let largeCardInteriorOptions = cardInfo.largeCardInteriorOptions;
        if (hasData) {
            let navFromUrl = this._router.url.split('?')[0];
            let chartData = this.setDonutChartData(data, cardInfo.name, cardInfo.type, params);
            let chartOptions = this._loanCharService.getDonutChartOptions(
                chartData,
                largeCardInteriorOptions.accessToUrl,
                navFromUrl
            );
            largeCardInteriorOptions.showCardControlArea = true;
            largeCardInteriorOptions.chart = new Highcharts.Chart(
                largeCardInteriorOptions.uniqueId,
                chartOptions
            );
        }
        largeCardInteriorOptions.heightPercent = 50;
        largeCardInteriorOptions.hasData = hasData;
        largeCardInteriorOptions.isDataReady = true;
        largeCardInteriorOptions.action = action;
        cardInfo.largeCardInteriorOptions = Object.assign({}, cardInfo.largeCardInteriorOptions);
    }

    setDonutChartData(
        data: IOperationsData[],
        title: string,
        filterType: string,
        params: IPipelineParameters
    ) {
        return data.map((i) => {
            let point = i[filterType];
            let color = '';

            if (this._loanCharService.colorMap.has(point.toLowerCase())) {
                color = this._loanCharService.colorMap.get(point.toLowerCase());
            }
            return {
                name: point,
                color: color,
                y: i.sumLoanAmount,
                properties: {
                    units: i.loanCount,
                    volume: i.sumLoanAmount,
                    // milestone: params.milestone,
                    // loanType: params.loanType,
                    milestone: 'All',
                    loanType: 'All',
                    filterType: filterType,
                    filterValue: point,
                    //loanPurpose: '',
                    titleSuffix: `: ${point}`,
                },
            };
        });
    }

    prepareSmallCards(
        data: IOperationsData[],
        cardInfo: ICardInfo,
        action: NoDataActionType,
        commonParams: IPipelineParameters
    ) {
        let card = cardInfo.card;
        card.action = action;
        card.hasData = data.length > 0;
        card.isDataReady = true;
        card.showCardControllers = true;
        card.cardId = cardInfo.cardId;

        if (card.hasData) {
            let titleSuffix = '';
            let isCardSevenDayEstimated = card.title === '7 Day Estimated';
            if (isCardSevenDayEstimated) {
                titleSuffix = ` - Not ${data[0].milestoneNotReached}`;
                card.title += titleSuffix;
            } else {
                titleSuffix = card.title;
            }

            let queryParams = {
                lockStatus: 'All',
                milestone: commonParams.milestone,
                acctLedgerType: commonParams.acctLedgerType,
                titleSuffix: titleSuffix,
            };

            let totalLoans = data.reduce((a, { countLoans }) => a + countLoans, 0);
            card.urlSegment = cardInfo.urlSegment;
            card.queryParams = queryParams;
            card.mainValue = isCardSevenDayEstimated
                ? totalLoans
                : this._reusableFunctions.formatMoney(data[0].sumFeeAmount);
        }
        cardInfo.card = Object.assign({}, card);
    }

    prepareBarChartCards(data: IOperationsData[], cardInfo: ICardInfo, action: NoDataActionType) {
        let hasData = data.length > 0;
        let largeCardInteriorOptions = cardInfo.largeCardInteriorOptions;
        largeCardInteriorOptions.action = action;
        largeCardInteriorOptions.hasData = hasData;
        largeCardInteriorOptions.isDataReady = true;
        largeCardInteriorOptions.showCardControlArea = true;

        if (hasData) {
            let barData = [];
            if (cardInfo.cardId === 'pipeline-ops-disclosure') {
                let disclosurePipelineDefinitions = [
                    {
                        key: 'stageOneLoanCount',
                        name: 'Day 1',
                        color: '#7C00A0',
                        disclosureStage: 1,
                    },
                    {
                        key: 'stageTwoLoanCount',
                        name: 'Day 2',
                        color: '#7EE552',
                        disclosureStage: 2,
                    },
                    {
                        key: 'stageThreeLoanCount',
                        name: 'Day 3',
                        color: '#F16E1C',
                        disclosureStage: 3,
                    },
                    {
                        key: 'stageFourLoanCount',
                        name: '> 3 Days',
                        color: 'red',
                        disclosureStage: 4,
                    },
                ];
                for (const disc of disclosurePipelineDefinitions) {
                    barData.push({
                        y: data[0][disc.key],
                        name: disc.name,
                        color: disc.color,
                        properties: {
                            title: disc.name,
                            disclosureStage: disc.disclosureStage,
                            reportURL: largeCardInteriorOptions.accessToUrl,
                        },
                    });
                }
            } else {
                let expiresSevenDaysDefinitions = {
                    0: '#7cb5ec',
                    1: '#FF0000',
                    2: '#FFC000',
                    3: '#FFFF00',
                    4: '#002060',
                    5: '#0070C0',
                    6: '#33CCCC',
                    7: '#00B050',
                };

                for (let loan of data) {
                    const daysToExpiration =
                        loan.daysToExpiration > 1 || loan.daysToExpiration === 0
                            ? `${loan.daysToExpiration} Days`
                            : `${loan.daysToExpiration} Day`;
                    barData.push({
                        y: loan.countLoans,
                        name: daysToExpiration,
                        color: expiresSevenDaysDefinitions[loan.daysToExpiration],
                        properties: {
                            title: daysToExpiration,
                            reportURL: largeCardInteriorOptions.accessToUrl,
                            expirationDays: loan.daysToExpiration,
                        },
                    });
                }
            }
            const chartOptions = this.getBarChartOptions(cardInfo.name, barData);
            largeCardInteriorOptions.chart = new Highcharts.Chart(
                largeCardInteriorOptions.uniqueId,
                chartOptions
            );
        }
        cardInfo.largeCardInteriorOptions = Object.assign({}, cardInfo.largeCardInteriorOptions);
    }

    getBarChartOptions(title, data): Highcharts.Options {
        const _this = this;
        return {
            chart: {
                type: 'bar',
                scrollablePlotArea: {
                    scrollPositionX: 0,
                    opacity: 1,
                },
                marginRight: 30,
            },
            exporting: {
                enabled: false,
            },
            title: {
                text: title,
                align: 'left',
            },
            subtitle: {
                text: '',
                align: 'left',
            },
            xAxis: {
                type: 'category',
                title: {
                    text: null,
                },
            },
            yAxis: {
                allowDecimals: false,
                title: {
                    text: '# of Loans',
                    align: 'high',
                },
                labels: {
                    overflow: 'justify',
                },
            },
            tooltip: {
                style: {
                    color: '#223540',
                    fontFamily: 'Roboto',
                    fontSize: '20',
                    height: 12,
                },
                borderColor: 'white',
                borderRadius: 6,
                backgroundColor: '#FFFFFF',
                useHTML: true,
                followPointer: true,
                distance: 15,
                formatter: function () {
                    const tooltipValue = `Loans: ${this.y}`;
                    return `<div class="operations-pipeline-container">
                        <div class="operations-pipeline-tooltip-title">
                            <div class="operations-pipeline-tooltip-name">${this.point.properties.title}</div>
                        </div>
                        <div class="operations-pipeline-tooltip-title operations-pipeline-tooltip-value" style="border-bottom: none">
                            <div class="operations-pipeline-tooltip-icon" style="color: ${this.point.color}">•</div>
                            <div style="font-size: 12px;">${tooltipValue}</div>
                        </div>
                    </div>`;
                },
            },
            plotOptions: {
                bar: {
                    dataLabels: {
                        enabled: true,
                    },
                },
                series: {
                    point: {
                        events: {
                            click: function (e) {
                                let queryParams = {
                                    lockStatus: 'All',
                                    titleSuffix: ': ' + this.properties.title,
                                };
                                if (this.properties.disclosureStage)
                                    queryParams['disclosureStage'] =
                                        this.properties.disclosureStage;
                                if (this.properties.expirationDays) {
                                    queryParams['expirationDays'] = this.properties.expirationDays;
                                    queryParams.lockStatus = 'L';
                                }
                                _this._navigationHelpService.navigateTo(
                                    [this.properties.reportURL],
                                    queryParams,
                                    true
                                );
                            },
                        },
                    },
                    cursor: 'pointer',
                } as Highcharts.PlotSeriesOptions,
            },
            legend: {
                enabled: false,
            },
            credits: {
                enabled: false,
            },
            series: [
                {
                    data: data,
                    type: 'bar',
                },
            ],
        };
    }

    setOperationsTableCardData(
        displayOptions: DisplayOptions[],
        hierarchyFilters: string,
        cardInfo: ICardInfo,
        action: NoDataActionType
    ) {
        let commonParams = this.getDisplayOptionDataPipelineOperations(
            displayOptions,
            hierarchyFilters
        );
        commonParams.cardId = cardInfo.type;

        let cardOptions = cardInfo.cardTableInfo;
        cardOptions.isDataReady = false;
        cardOptions.hasData = false;
        cardOptions.cardId = cardInfo.cardId;
        cardInfo.cardTableInfo = Object.assign({}, cardOptions);

        this.getPipelineOperationsData(commonParams).subscribe(
            (res) => {
                this.prepareTableCard(res, cardInfo, action, commonParams);
            },
            (error) => {
                this.prepareTableCard([], cardInfo, NoDataActionType.error, commonParams);
                console.log('error: ', error);
            }
        );
    }

    prepareTableCard(
        data: IOperationsData[],
        tableCard: ICardInfo,
        action: NoDataActionType,
        commonParams
    ) {
        let largeCardTableOptions = tableCard.cardTableInfo;
        let tableInfo: ITableTdItems[] = [
            {
                headerTitle: 'Milestone',
                fieldId: 'milestone',
                isHyperlinked: true,
                headerStyle: {
                    'text-align': 'left',
                    color: '#333333',
                    'text-transform': 'uppercase',
                    width: '46%',
                },
                style: {
                    'font-weight': '500',
                    'text-align': 'left',
                    'font-size': '14px',
                    width: '46%',
                },
            },
            {
                headerTitle: 'Volume',
                fieldId: 'currentVolume',
                isHyperlinked: false,
                headerStyle: {
                    'text-transform': 'uppercase',
                    'text-align': 'right',
                    color: '#333333',
                    width: '27%',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '14px',
                    width: '27%',
                    height: '40px',
                },
            },
            {
                headerTitle: 'Units',
                fieldId: 'currentUnits',
                isHyperlinked: false,
                headerStyle: {
                    'text-transform': 'uppercase',
                    'text-align': 'right',
                    color: '#333333',
                    width: '27%',
                },
                style: {
                    'text-align': 'right',
                    'font-size': '14px',
                    width: '27%',
                },
                showIndicator: true,
                compareFieldId: 'previousMonthlyValue',
                diffField: 'monthlyDiff',
            },
        ];

        let hasData = data && data.length > 0;

        if (hasData) {
            tableCard.cardTableInfo.dataTableData = [];

            let tableRowItems: IRowItems[] = [];
            const totalVolume = data.reduce((a, { currentVolume }) => a + currentVolume, 0);
            const totalUnits = data.reduce((a, { currentUnits }) => a + currentUnits, 0);
            const unitPercents = this._reusableFunctions.getRefinedPercents(
                data.map((number) => (number.currentUnits / totalUnits) * 100)
            );
            const volumePercents = this._reusableFunctions.getRefinedPercents(
                data.map((number) => (number.currentVolume / totalVolume) * 100)
            );

            data.forEach((item, i) => {
                let dataItem = Object.assign({}, item);
                let tableTdItems = [];
                tableInfo.forEach((rowInf) => {
                    let rowInfCopy = Object.assign({}, rowInf);
                    let value = dataItem[rowInfCopy.fieldId];

                    if (rowInfCopy.fieldId == 'currentVolume') {
                        rowInfCopy.percent = unitPercents[i];
                        value = this._reusableFunctions.formatNumber(value, 0, 'currency');
                    } else if (rowInfCopy.fieldId == 'currentUnits') {
                        rowInfCopy.percent = volumePercents[i];
                        value = this._reusableFunctions.formatNumber(value);
                    }

                    /// TODO: pass these values as parameters
                    if (rowInfCopy.fieldId == 'milestone') {
                        rowInfCopy.urlSegment = `ops-active-pipeline`;
                        rowInfCopy.routerLink = `/reporting/pipeline/`;
                        rowInfCopy.hasAccess = true;
                        rowInfCopy.queryParams = {
                            milestone: value,
                            lockStatus: 'All',
                            titleSuffix: `: ${value}`,
                            navFromUrl: 'pipeline/insights/operations-pipeline',
                        };
                    }

                    rowInfCopy.value = value;
                    tableTdItems.push(rowInfCopy);
                });

                tableRowItems.push({
                    tdList: tableTdItems,
                });
            });

            largeCardTableOptions.dataTableData = tableRowItems;
            largeCardTableOptions.showCardControllers = true;
        }

        largeCardTableOptions.hasData = hasData;
        largeCardTableOptions.isDataReady = true;
        largeCardTableOptions.action = action;
        tableCard.cardTableInfo = Object.assign({}, largeCardTableOptions);
    }
}
